import { useI18n } from "i18n";
import classNames from "classnames";
import { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { Replay } from "@remo-co/ui-core/src/icons/Replay";
import { useTicker } from "modules/dateTime";
import { SUPPORT_STATUS } from "modules/systemCheck/constants";
import { selectIsMobile } from "modules/deviceInfo";
import { useAppDispatch } from "store/hooks";
import { SystemCheckStatusIcon } from "../SystemCheckStatusIcon";
import { FirebaseDetectionError } from "../FirebaseDetectionError";
import { BrowserDetection } from "../BrowserDetection";
import { FirewallDetection } from "../FirewallDetection";
import { OperatingSystemDetection } from "../OperatingSystemDetection";
import { VPNDetection } from "../VPNDetection";
import {
  selectAdBlockResult,
  selectDailyResult,
  selectDisableVPNCheck,
  selectFirebaseResult,
  selectFirebaseStorageResult,
  selectFirestoreResult,
  selectHaveChecksBeenRun,
  selectIsBrowserSupported,
  selectIsLoading,
  selectReachabilityResult,
  selectSystemInfo,
  selectSystemStatus,
  selectVPNDetectionResult,
} from "../../redux/selectors";
import { AdBlockDetection } from "../AdBlockDetection";
import { setAllDirty } from "../../redux/systemCheckSlice";
import { useStyles } from "./styles";
import { DailyDetectionError } from "../DailyDetectionError";

export const PERFORM_SYSTEM_CHECK_INTERVAL_MS = 1000 * 60 * 10;

const SystemCheckNotifications = (): ReactElement | null => {
  const styles = useStyles();
  const { t } = useI18n(["common", "micCamCheck"]);
  const dispatch = useAppDispatch();

  const isMobileView = useSelector(selectIsMobile);
  const isLoading = useSelector(selectIsLoading);
  const [isRetrying, setIsRetrying] = useState(false);
  const systemStatus = useSelector(selectSystemStatus);
  const isBrowserSupported = useSelector(selectIsBrowserSupported);
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const { browserName, browserSupport, osName, osSupported } =
    useSelector(selectSystemInfo);
  const firebaseResult = useSelector(selectFirebaseResult);
  const dailyResult = useSelector(selectDailyResult);
  const firestoreResult = useSelector(selectFirestoreResult);
  const firebaseStorageResult = useSelector(selectFirebaseStorageResult);
  const adBlockResult = useSelector(selectAdBlockResult);
  const vpnResult = useSelector(selectVPNDetectionResult);
  const disableVPNCheck = useSelector(selectDisableVPNCheck);
  const reachabilityResult = useSelector(selectReachabilityResult);
  const haveChecksBeenRun = useSelector(selectHaveChecksBeenRun);

  const initialTick = 0;
  const tokenTicker = useTicker(
    true,
    PERFORM_SYSTEM_CHECK_INTERVAL_MS,
    initialTick,
  );

  const hasAnyNetworkCheckFailed =
    firestoreResult.isSuccessful === false ||
    firebaseStorageResult.isSuccessful === false ||
    Object.values(reachabilityResult).some(
      (result) => result.isSuccessful === false,
    );

  const showRetryButton =
    firebaseResult.isSuccessful === false ||
    dailyResult.isSuccessful === false ||
    hasAnyNetworkCheckFailed ||
    adBlockResult.isSuccessful === false ||
    vpnResult.isSuccessful === false;

  useEffect(() => {
    if (!isLoading) {
      setIsRetrying(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (
      !haveChecksBeenRun ||
      (tokenTicker !== initialTick && isNotificationVisible)
    ) {
      dispatch(
        setAllDirty({
          value: true,
          skipDailyTest: true,
        }),
      );
    }

    // Tests should be re-run only when ticker value changes.
    // Re-running useEffect when isNotificationVisible changes will cause unexpected behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenTicker]);

  const handleClose = () => {
    setIsRetrying(false);
    setIsNotificationVisible(false);
  };

  const handleRetest = () => {
    setIsRetrying(true);
    dispatch(
      setAllDirty({
        value: true,
        skipDailyTest: true,
      }),
    );
  };

  const getButtonLabel = () => {
    if (isLoading) return t("micCamCheck:testing");

    return showRetryButton ? t("common:retry") : t("micCamCheck:close");
  };

  if (
    systemStatus === SUPPORT_STATUS.SUPPORTED ||
    !isNotificationVisible ||
    isMobileView ||
    !haveChecksBeenRun ||
    (isLoading && !isRetrying)
  )
    return null;

  const headerClassNames = classNames(
    styles.header,
    systemStatus === SUPPORT_STATUS.PARTIALLY_SUPPORTED
      ? styles.headerRecommendation
      : styles.headerError,
  );

  return (
    <div
      className={styles.systemCheckNotifications}
      data-testid="systemCheckNotifications"
    >
      <div className={headerClassNames}>
        <div className={styles.title}>
          <div className={styles.statusIcon}>
            <SystemCheckStatusIcon status={systemStatus} />
          </div>
          <Typography variant="body1">
            {t(
              systemStatus === SUPPORT_STATUS.UNSUPPORTED
                ? "micCamCheck:system.check.detected.issues"
                : "micCamCheck:system.check.recommendations",
            )}
          </Typography>
        </div>
        <IconButton
          aria-label={t("common:modal.window.aria.label")}
          data-testid="close-button"
          className={styles.closeBtn}
          onClick={handleClose}
          size="large"
        >
          <Close />
        </IconButton>
      </div>
      <div className={styles.body}>
        {isRetrying && (
          <div className={styles.flexCenter}>
            <Loader data-testid="progress" size={16} />
          </div>
        )}
        {!isRetrying && systemStatus === SUPPORT_STATUS.UNSUPPORTED && (
          <>
            <FirebaseDetectionError
              isFirebaseAccessible={firebaseResult.isSuccessful ?? false}
              isFirestoreAccessible={firestoreResult.isSuccessful ?? false}
              isRequiredChecksLoading={isLoading}
            />
            <DailyDetectionError
              isDailyAccessible={dailyResult.isSuccessful ?? false}
              isRequiredChecksLoading={isLoading}
            />
          </>
        )}
        {!isRetrying && systemStatus === SUPPORT_STATUS.PARTIALLY_SUPPORTED && (
          <>
            {hasAnyNetworkCheckFailed && (
              <FirewallDetection
                isLoading={false}
                isFirebaseStorageAccessible={
                  firebaseStorageResult.isSuccessful ?? false
                }
                reachabilityResult={reachabilityResult}
                hasDivider={false}
              />
            )}
            {adBlockResult.isSuccessful === false && (
              <AdBlockDetection
                isLoading={false}
                isAdBlockEnabled
                hasDivider={false}
                isNotification
              />
            )}
            {!disableVPNCheck && vpnResult.isSuccessful === false && (
              <VPNDetection
                isLoading={false}
                isVPNDetected
                hasDivider={false}
                isNotification
              />
            )}
            {!osSupported && (
              <OperatingSystemDetection
                os={osName}
                isSupported={osSupported}
                hasDivider={false}
              />
            )}
            {!isBrowserSupported && (
              <BrowserDetection
                browserSupport={browserSupport}
                browser={browserName}
                hasDivider={false}
              />
            )}
          </>
        )}
      </div>
      <div className={styles.footer}>
        <Button
          variant="secondary"
          color="dark"
          size="md"
          contentClassName={styles.actionButtonContent}
          onClick={showRetryButton ? handleRetest : handleClose}
          disabled={isLoading}
        >
          {showRetryButton && <Replay className={styles.retryIcon} />}
          {getButtonLabel()}
        </Button>
      </div>
    </div>
  );
};

export default SystemCheckNotifications;
